import type { LinkPreviewData } from '../components/LinkPreview/types';
import { getYouTubeMetadata } from './youtube';
import { cacheGet, cacheSet } from './cache';
import { getRandomFallbackImage } from './fallback';
import axios from 'axios';

const CORS_PROXY = 'https://api.allorigins.win/get?url=';

const extractMetadata = (html: string, url: string): LinkPreviewData => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  const getMetaContent = (selectors: string[]): string | null => {
    for (const selector of selectors) {
      const element = doc.querySelector(selector);
      const content = element?.getAttribute('content') || element?.getAttribute('value');
      if (content) return content;
    }
    return null;
  };

  const title = 
    getMetaContent(['meta[property="og:title"]', 'meta[name="twitter:title"]']) ||
    doc.querySelector('title')?.textContent ||
    new URL(url).hostname;

  const description = 
    getMetaContent([
      'meta[property="og:description"]',
      'meta[name="twitter:description"]',
      'meta[name="description"]'
    ]) || '';

  const image = 
    getMetaContent([
      'meta[property="og:image"]',
      'meta[property="og:image:url"]',
      'meta[name="twitter:image"]'
    ]) || getRandomFallbackImage('article');

  const type = 
    getMetaContent(['meta[property="og:type"]']) || 'article';

  return {
    title,
    description,
    image,
    url,
    type
  };
};

const fetchWithProxy = async (url: string): Promise<LinkPreviewData> => {
  try {
    const response = await axios.get(`${CORS_PROXY}${encodeURIComponent(url)}`);
    const contents = response.data.contents;
    return extractMetadata(contents, url);
  } catch (error) {
    throw new Error('Failed to fetch with proxy');
  }
};

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const fetchMetadata = async (url: string): Promise<LinkPreviewData> => {
  if (!isValidUrl(url)) {
    return {
      title: 'Invalid URL',
      description: 'The provided URL is not valid',
      image: getRandomFallbackImage(),
      url: url,
      type: 'website'
    };
  }

  // Check cache first
  const cachedData = cacheGet<LinkPreviewData>(url);
  if (cachedData) return cachedData;

  try {
    let metadata: LinkPreviewData;

    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      metadata = await getYouTubeMetadata(url);
    } else {
      try {
        metadata = await fetchWithProxy(url);
      } catch (error) {
        console.error('Proxy fetch failed:', error);
        metadata = {
          title: new URL(url).hostname,
          description: 'No description available',
          image: getRandomFallbackImage('article'),
          url: url,
          type: 'article'
        };
      }
    }

    if (metadata.title !== new URL(url).hostname) {
      cacheSet(url, metadata);
    }
    
    return metadata;
  } catch (error) {
    console.error('Error fetching metadata:', error);
    
    return {
      title: new URL(url).hostname,
      description: 'No description available',
      image: getRandomFallbackImage('article'),
      url: url,
      type: 'article'
    };
  }
};